import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";

import Caret, { CaretPosition } from "@components/caret/caret";
import {
  CharacterHandle,
  CharacterLottieWeb,
} from "@components/character/character-lottie-web";
import Tooltip, { TooltipPosition } from "@components/tooltip/tooltip";
import { useFontSizeDetect } from "@components/unfocused-progress/hooks/use-font-size-detect";

import { useAppSelector } from "@hooks/use-store";
import { CHARACTERS } from "@constants/characters.constant";
import { ExerciseCompleteData } from "@typings/global.types";

import { IGetExerciseEntrypointApiResponse } from "../../api/exercise-entrypoint/exercise-entrypoint.types";

type ExerciseProgressProps = {
  data: IGetExerciseEntrypointApiResponse | undefined;
  text: string;
  typedText: string;
  nextLetter: string;
  restText: string;
  currentLetterIndex: number;
  isOverlayVisible: boolean;
  isTooltipVisible: boolean;
  focusFunction: () => void;
  characterRef: React.MutableRefObject<null | CharacterHandle>;
  overlayNode: React.ReactNode;
};

function ExerciseProgress({
  text,
  data,
  typedText,
  nextLetter,
  restText,
  currentLetterIndex,
  isOverlayVisible,
  isTooltipVisible,
  focusFunction,
  characterRef,
  overlayNode,
}: ExerciseProgressProps) {
  const { errorKey, activeCharacterName } = useAppSelector(
    (state) => state.app,
  );
  const isAuthorized = Boolean(data);
  const textContainerRef = useRef<HTMLDivElement>(null);
  const nextLetterRef = useRef<HTMLSpanElement>(null);
  const prevExerciseId = sessionStorage.getItem("prevId");
  const [fontSizeValue, setFontSizeValue] = useState<number>(0);
  const [characterHeightValue, setCharacterHeightValue] = useState<number>(0);
  const [characterWidthValue, setCharacterWidthValue] = useState(0);
  const {
    getFontSize,
    calculateCharacterHeightByFontSize,
    calculateCharacterWidthByHeight,
  } = useFontSizeDetect(text, 70);

  const [isPastExercise, setIsPastExercise] = useState<boolean>(false);
  const [exerciseCompleteData, setExerciseCompleteData] =
    useState<ExerciseCompleteData | null>(null);

  const activeCharacter = activeCharacterName
    ? CHARACTERS[activeCharacterName]
    : null;

  const character = activeCharacter ? (
    <CharacterLottieWeb
      key={`${activeCharacterName}-${characterHeightValue}`}
      character={activeCharacter}
      ref={characterRef}
      width={characterWidthValue}
      height={characterHeightValue}
    />
  ) : null;

  useEffect(() => {
    const fontSizeResizeHandler = () => {
      if (!textContainerRef.current) return;
      const fontSize = getFontSize(textContainerRef.current?.offsetWidth);
      setFontSizeValue(fontSize);
      const height = calculateCharacterHeightByFontSize(fontSize);
      setCharacterHeightValue(height);
      setCharacterWidthValue(
        calculateCharacterWidthByHeight(height, activeCharacterName),
      );
    };
    fontSizeResizeHandler();
    window.addEventListener("resize", fontSizeResizeHandler);
    return () => {
      window.removeEventListener("resize", fontSizeResizeHandler);
    };
  }, [
    activeCharacterName,
    calculateCharacterHeightByFontSize,
    calculateCharacterWidthByHeight,
    getFontSize,
    text,
    textContainerRef.current?.offsetWidth,
  ]);

  const isCursorEnabled = character !== null;
  const isLastLetter = currentLetterIndex === text.length;

  const [caretPosition, setCaretPosition] = useState<CaretPosition>({
    x: 0,
    y: 0,
  });
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition>({
    top: 0,
    left: 0,
  });
  useEffect(() => {
    if (currentLetterIndex === 0) {
      setCaretPosition({ x: 0, y: 0 });
    }
    if (nextLetterRef.current && currentLetterIndex) {
      setCaretPosition({
        x: nextLetterRef.current.offsetLeft,
        y: isLastLetter ? 0 : nextLetterRef.current.offsetTop,
      });
    }
  }, [currentLetterIndex, fontSizeValue, activeCharacterName, isLastLetter]);

  useEffect(() => {
    if (!nextLetterRef.current) return;
    setTooltipPosition({
      top: -fontSizeValue,
      left: nextLetterRef.current.offsetLeft,
    });
  }, [currentLetterIndex, fontSizeValue, activeCharacterName]);

  return (
    <div className='bg-gradient-on-test dark:bg-gradient-on-test-dark -mx-6 pt-8 pb-[70px] text-black dark:text-white'>
      <div
        role='presentation'
        onClick={focusFunction}
        className='relative mx-4 bg-white p-4 text-center shadow-textarea focus:outline dark:bg-black md:mx-6 md:rounded-lg'
      >
        {overlayNode}
        {isPastExercise ? (
          <div
            style={character ? { minHeight: "70px" } : { minHeight: "40px" }}
          />
        ) : (
          <div
            ref={textContainerRef}
            className={classNames(
              "transition-filter flex items-center justify-center font-source-code font-bold",
              {
                "opacity-0": fontSizeValue === 0,
                "blur-none": !isOverlayVisible,
                "blur-10": isOverlayVisible,
              },
            )}
            style={{
              fontSize: `${fontSizeValue}px`,
              lineHeight: `calc(${fontSizeValue}px + 4px)`,
            }}
          >
            <div
              className={classNames(
                "relative mx-auto inline-flex items-center justify-center",
                {
                  "bg-key-error": errorKey !== "",
                },
              )}
            >
              <Tooltip
                showTooltip={
                  currentLetterIndex === 0 &&
                  isTooltipVisible &&
                  !isOverlayVisible
                }
                tooltipPosition={tooltipPosition}
                textId='start-typing'
              />
              <span
                className={classNames(
                  "dark:text-grey-dark-dark text-grey-light",
                  {
                    "-z-10 opacity-0": isCursorEnabled,
                  },
                )}
              >
                {typedText}
              </span>
              {character || <Caret caretPosition={caretPosition} />}
              <span ref={nextLetterRef} className='letter'>
                {nextLetter}
              </span>
              <span className=''>{restText}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ExerciseProgress;
